<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '#modules/auth/composables/usePermissions.js';
import TeamSidebarGroup from '#modules/team/components/TeamSidebarGroup.vue';
import CFSidebar from '@/shared/components/CFSidebar.vue';
import { AppPermissions } from '#modules/auth/constants/permissions.js';

const { t } = useI18n();
const { canAll } = usePermissions();

const groups = [
    {
        title: t('team.sidebar.details'),
        items: [
            {
                title: t('team.sidebar.company_details'),
                href: { name: 'team' },
                icon: 'common-file-text-info',
            },
        ],
    },
    {
        title: t('team.sidebar.members'),
        items: [
            {
                title: t('team.sidebar.team_members'),
                href: { name: 'team members' },
                icon: 'multiple-neutral1',
                permissions: [AppPermissions.ManageTeams],
            },
            {
                title: t('team.sidebar.default_language'),
                href: { name: 'team default language' },
                icon: 'chat-translate',
                permissions: [AppPermissions.ManageTeams],
            },
        ],
    },
    {
        title: t('team.sidebar.financial'),
        items: [
            {
                title: t('team.sidebar.direct_debit'),
                href: { name: 'team direct debit' },
                icon: 'cash-payment-bag2',
                permissions: [AppPermissions.ManageTeamPayments],
            },
            {
                title: t('team.sidebar.subscriptions'),
                href: { name: 'subscriptions' },
                icon: 'office-drawer',
                permissions: [AppPermissions.ManageFinancial],
            },
        ],
    },
    {
        title: t('team.sidebar.notifications'),
        items: [
            {
                title: t('team.sidebar.slack_integration'),
                href: { name: 'team slack integration' },
                icon: 'conversation-chat1',
                permissions: [AppPermissions.ManageNotificationSettings],
            },
            {
                title: t('team.sidebar.notification_settings'),
                href: { name: 'team notifications' },
                icon: 'bell',
                permissions: [AppPermissions.ManageNotificationSettings],
            },
        ],
    },
    {
        title: t('team.sidebar.security'),
        items: [
            {
                title: t('team.sidebar.enforce_2fa'),
                href: { name: 'team two factor authentication' },
                icon: 'lock',
                permissions: [AppPermissions.ManageTeams],
            },
            {
                title: t('team.sidebar.activity_log'),
                href: { name: 'team activity log' },
                icon: 'logs-authentication-stack-document',
                permissions: [AppPermissions.ManageTeams, AppPermissions.ManageActivities],
            },
            {
                title: t('team.sidebar.ssh_keys'),
                href: { name: 'team ssh keys' },
                icon: 'login-keys',
                permissions: [AppPermissions.ManageWebOpsSshKeys],
            },
        ],
    },
];

const filteredGroups = computed(() => {
    return groups
        .map((group) => {
            const filteredItems = group.items.filter((item) => {
                if (!item.permissions || !Array.isArray(item.permissions)) {
                    return true;
                }

                return canAll(item.permissions);
            });

            return { ...group, items: filteredItems };
        })
        .filter((group) => {
            // Hide groups with no visible items
            return group.items.length > 0;
        });
});
</script>

<template>
    <CFSidebar>
        <div class="px-8 pt-12 pb-10">
            <h3 class="mb-8 text-lg font-bold">{{ $t('general.team') }}</h3>

            <template
                v-for="(group, index) in filteredGroups"
                :key="index"
            >
                <TeamSidebarGroup
                    :title="group.title"
                    :items="group.items"
                />

                <hr
                    v-if="index !== filteredGroups.length - 1"
                    class="my-5"
                />
            </template>
        </div>
    </CFSidebar>
</template>
